import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft, Smartphone, ShieldCheck, Lock, Zap } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { createSendOtp, verifyOtpLogin } from "../../services/authServices";
import { notify } from "../../components/Common/Alerts";
import Utils from "../../utils";
import { decryptData } from "../../utils/crypto";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { authActions, store } from "../../store";
import CONFIG from "../../config";
import { maskMobile } from "../../utils/function";
import { useConfig } from "../../components/Context/ConfigContext";

const OTP = () => {
  const inputRefs = useRef([]);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { config, loading } = useConfig();
  const OTP_TIME = config?.REACT_OTP_RESEND || 30;

  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(OTP_TIME);
  const [isActive, setIsActive] = useState(true);

  const source = location.state?.data;

  if (!source?.mobile) {
    return <Navigate to="/login" replace />;
  }

  const startTimer = () => {
    Cookies.set("otp_expiry", Date.now() + 30 * 1000, {
      expires: 1 / 1440,
      path: "/",
    });

    setTimeLeft(30);
    setIsActive(true);
  };

  useEffect(() => {
    Cookies.remove("otp_expiry", { path: "/" });
    startTimer();
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsActive(false);
          Cookies.remove("otp_expiry", { path: "/" });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]);

  const handleSubmit = (e, otpOverride) => {
    if (e) e.preventDefault();

    if (isLoading) return;

    const otpValue = otpOverride || otp.join(""); // FIX

    if (otpValue.length !== 6) return;

    let obj = {
      mobile: source?.mobile || "",
      role_id: source?.role_id,
      device_token: source?.device_token,
      device_type: source?.device_type,
      name: source?.name || "",
      otp: otpValue,
    };

    setIsLoading(true);

    verifyOtpLogin(obj)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        let userData = decrypted?.user;

        if (userData) {
          Utils.setDetail(decrypted);
          Utils.setIntervalRemove();
          store.dispatch(authActions.login(decrypted));

          if (
            decrypted.is_store_selected === 0 &&
            Number(userData.role_id) === CONFIG.ROLE_OWNER
          ) {
            navigate("/store-add");
          } else {
            navigate("/dashboard");
          }
        }

        if (source?.name) {
          notify.success(decrypted.message);
          Utils.setDetail(decrypted);
          Utils.setIntervalRemove();
          store.dispatch(authActions.login(decrypted));
          navigate("/dashboard");
        }
      })
      .catch((error) => {
        notify.error(Utils.getErrorMessage(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (element, index) => {
    const value = element.value;

    if (value?.length > 1) {
      const pasted = value.slice(0, 6).split("");
      const newOtp = [...otp];

      pasted.forEach((char, i) => {
        if (!isNaN(char)) {
          newOtp[i] = char;
        }
      });

      setOtp(newOtp);

      const lastIndex = pasted.length - 1;
      inputRefs.current[lastIndex]?.focus();

      const fullOtp = newOtp.join("");

      if (fullOtp.length === 6 && !newOtp.includes("")) {
        setTimeout(() => handleSubmit(null, fullOtp), 100);
      }

      return;
    }

    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    const fullOtp = newOtp.join("");

    if (fullOtp.length === 6 && !newOtp.includes("")) {
      setTimeout(() => handleSubmit(null, fullOtp), 100); // 👈 FIX
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];

      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const handleResend = () => {
    setOtp(new Array(6).fill(""));

    setIsResendLoading(true);
    let obj = {
      mobile: source?.mobile || "",
      role_id: source?.role_id,
    };

    createSendOtp(obj)
      .then(() => {
        notify.success("OTP resent successfully");
        startTimer();
        setIsResendLoading(false);
      })
      .catch((err) => {
        setIsResendLoading(false);

        notify.error(Utils.getErrorMessage(err));
      });
  };

  useEffect(() => {
    if ("OTPCredential" in window) {
      const ac = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ["sms"] },
          signal: ac.signal,
        })
        .then((otpCredential) => {
          if (otpCredential && otpCredential.code) {
            const code = otpCredential.code.split("");
            setOtp(code);

            code.forEach((digit, i) => {
              if (inputRefs.current[i]) {
                inputRefs.current[i].value = digit;
              }
            });
          }
        })
        .catch(() => {});

      return () => ac.abort();
    }
  }, []);

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();

    if (!/^\d+$/.test(pasteData)) return;

    const newOtp = pasteData.slice(0, otp.length).split("");

    setOtp((prev) => {
      const updated = [...prev];
      newOtp.forEach((val, i) => {
        updated[i] = val;
      });
      return updated;
    });

    const lastIndex = newOtp.length - 1;
    if (inputRefs.current[lastIndex]) {
      inputRefs.current[lastIndex].focus();
    }
  };

  const isOtpValid = otp.join("").length === 6 && !otp.includes("");

  return (
    <div className="min-h-screen w-full flex overflow-hidden bg-brand-body">
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 md:p-16 relative animate-fade-up">
        <div className="w-full max-w-md">
          <button
            type="button"
            onClick={() => {
              Cookies.remove("otp_expiry", { path: "/" });
              setOtp(new Array(6).fill(""));
              setTimeLeft(0);
              setIsActive(false);

              if (source?.name) {
                navigate("/customer-register");
              } else {
                navigate("/login", { state: source });
              }
            }}
            className="flex items-center text-slate-400 hover:text-brand-orange transition-all mb-12 group"
          >
            <div className="p-2 rounded-full group-hover:bg-orange-50 transition-colors">
              <ArrowLeft size={20} />
            </div>
            <span className="text-sm font-bold ml-1">Go Back</span>
          </button>

          <div className="mb-12">
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-orange-100 border border-orange-50">
              <Smartphone className="text-brand-orange" size={28} />
            </div>
            <h2 className="text-4xl font-black text-slate-900 tracking-tight mb-3">
              Verify OTP
            </h2>
            <p className="text-slate-500 font-medium text-lg">
              Sent to{" "}
              <span className="text-brand-orange">
                {maskMobile(source?.mobile)}
              </span>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-6 gap-3 mb-10">
              {otp?.map((data, index) => (
                <input
                  key={index}
                  name="otp"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  ref={(el) => (inputRefs.current[index] = el)}
                  maxLength={1}
                  value={data}
                  onChange={(e) => handleChange(e.target, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className="w-full aspect-square text-center text-2xl font-black border-2 border-slate-100 rounded-2xl focus:border-brand-orange focus:ring-4 focus:ring-orange-50 outline-none transition-all bg-white text-slate-800 shadow-sm"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={isLoading || !isOtpValid}
              className={`w-full bg-brand-gradient text-white font-black py-4 rounded-2xl shadow-xl shadow-orange-200 hover:scale-[1.02] active:scale-95 transition-all text-lg mb-8 uppercase tracking-widest flex items-center justify-center gap-3 ${
                isLoading || !isOtpValid ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>

                  <span>Processing...</span>
                </>
              ) : (
                "Confirm & Login"
              )}
            </button>
          </form>

          <div className="text-center py-4 rounded-2xl bg-orange-50/30 border border-dashed border-orange-200">
            {isActive ? (
              <p className="text-sm text-slate-500">
                Resend OTP in{" "}
                <span className="text-brand-orange font-bold">{timeLeft}s</span>
              </p>
            ) : (
              <button
                onClick={handleResend}
                disabled={isResendLoading}
                className={`
                       w-full flex items-center justify-center gap-2 transition-all duration-200
                        ${
                          isResendLoading
                            ? "border-2 border-dashed border-brand-orange/40 rounded-xl py-4 cursor-not-allowed"
                            : "text-brand-orange font-black hover:underline py-2"
                        }
                    `}
              >
                {isResendLoading ? (
                  <div className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-brand-orange"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    <span className="text-brand-orange font-medium">
                      Resending...
                    </span>
                  </div>
                ) : (
                  "Resend Now"
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT SIDE: Vibrant Orange Brand Section */}
      <div className="hidden lg:flex w-1/2 bg-brand-gradient relative items-center justify-center overflow-hidden">
        {/* Animated Visual Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/20 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-300/30 rounded-full blur-2xl animate-blob animation-delay-2000"></div>

        <div className="relative z-10 text-center px-12 animate-float">
          {/* Main Icon Glassmorphism */}
          <div className="w-36 h-36 bg-white/20 backdrop-blur-md rounded-[3rem] border border-white/40 flex items-center justify-center mx-auto mb-10 shadow-2xl">
            <div className="w-24 h-24 bg-white rounded-[2rem] flex items-center justify-center shadow-xl">
              <ShieldCheck className="text-brand-orange" size={48} />
            </div>
          </div>

          <h1 className="text-5xl font-black text-white mb-6 leading-tight tracking-tighter">
            Secure Your <br />
            Digital Empire
          </h1>

          <div className="inline-flex flex-col items-center gap-6">
            <div className="h-1.5 w-24 bg-white/40 rounded-full"></div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2 text-white font-bold text-sm bg-white/10 px-5 py-2.5 rounded-full backdrop-blur-sm border border-white/20">
                <Lock size={18} /> 100% SECURE
              </div>
              <div className="flex items-center gap-2 text-white font-bold text-sm bg-white/10 px-5 py-2.5 rounded-full backdrop-blur-sm border border-white/20">
                <Zap size={18} /> INSTANT
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Grid Overlay */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
      </div>
    </div>
  );
};

export default OTP;
